import { useState } from "react";
import { toast } from "sonner";
import { Check, Trash2, Loader2, CalendarClock } from "lucide-react";
import founderApi, { useFounder, errMsg, fmtD } from "@/lib/founderApi";
import { ChartPanel } from "@/components/founder/ui";
import { useConfirm } from "@/lib/confirm";

export function CompanyNotes({ orgId }) {
  const { data, reload } = useFounder(`/platform/companies/${orgId}/notes`);
  const [text, setText] = useState("");
  const ask = useConfirm();
  const [follow, setFollow] = useState("");
  const [busy, setBusy] = useState(false);
  const notes = Array.isArray(data) ? data : data?.notes || [];

  const act = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e, "Couldn't save the note.")); }
  };
  const add = async (e) => {
    e.preventDefault(); if (!text.trim()) return;
    setBusy(true);
    await act(() => founderApi.post(`/platform/companies/${orgId}/notes`, { text: text.trim(), follow_up: follow || null }), "Note saved.");
    setText(""); setFollow(""); setBusy(false);
  };

  return (
    <ChartPanel title="Notes & follow-ups" hint="Private to you" testid="company-notes">
      <form onSubmit={add} className="mb-4 space-y-2">
        <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Called Robert — card expired, will update Friday" data-testid="note-text"
          className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-400"><CalendarClock className="h-3.5 w-3.5" /> Follow up on
            <input type="date" value={follow} onChange={(e) => setFollow(e.target.value)} data-testid="note-followup" className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-white [color-scheme:dark]" />
          </label>
          <button type="submit" disabled={busy || !text.trim()} data-testid="note-add" className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {busy && <Loader2 className="h-3 w-3 animate-spin" />} Add note
          </button>
        </div>
      </form>
      <ul className="space-y-2">
        {notes.map((n) => (
          <li key={n.id} className={`rounded-lg border border-slate-800 p-3 text-sm ${n.done ? "opacity-50" : ""}`} data-testid={`note-${n.id}`}>
            <p className={`whitespace-pre-wrap text-slate-200 ${n.done ? "line-through" : ""}`}>{n.text}</p>
            <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500">
              <span>{fmtD(n.created_at)}</span>
              {n.follow_up && <span className="text-amber-300">Follow up {fmtD(n.follow_up)}</span>}
              <button onClick={() => act(() => founderApi.put(`/platform/notes/${n.id}`, { done: !n.done }))} className="ml-auto inline-flex items-center gap-1 hover:text-emerald-300" data-testid={`note-done-${n.id}`}><Check className="h-3 w-3" /> {n.done ? "Reopen" : "Done"}</button>
              <button onClick={async () => { if (await ask({ title: "Delete this note?", confirmLabel: "Delete", destructive: true })) act(() => founderApi.delete(`/platform/notes/${n.id}`), "Note deleted."); }} className="inline-flex items-center gap-1 hover:text-red-300" data-testid={`note-delete-${n.id}`}><Trash2 className="h-3 w-3" /> Delete</button>
            </div>
          </li>
        ))}
        {data && !notes.length && <li className="text-sm text-slate-500">No notes yet.</li>}
      </ul>
    </ChartPanel>
  );
}
